// =============================================================================
// app/src/lib/contasPagar.ts — a aritmética da tela de Contas a pagar.
//
// Pedido do dono (23/08/2026): selecionar contas com checkbox e ver o total;
// enxergar o vencimento de uma data específica com facilidade.
//
// Funções PURAS, com `hoje` injetado — testáveis em node sem navegador (o
// mesmo motivo do armazém do rascunho de venda). A tela só orquestra.
//
// A regra que atravessa tudo: desde a 046, `valor_previsto` pode ser NULL
// ("a definir" — a conta de luz que varia). Conta sem valor NÃO entra em
// lote (o lote não tem campo de valor por conta) e NÃO soma em subtotal —
// mas é CONTADA, para não sumir da conta de cabeça de quem lê.
// =============================================================================
import type { ContaPagar, PaymentMethod, PayrollEntry } from '@core/types';
import { faltaAComissao } from './folha';
import { hojeISO } from './formato';

/** O valor que a conta tem HOJE: o pago quando pagou, senão a previsão.
 *  Nulo = "a definir" (046). Substitui as sete repetições da tela. */
export function valorDaConta(c: Pick<ContaPagar, 'valor_real' | 'valor_previsto'>): number | null {
  const v = c.valor_real ?? c.valor_previsto;
  return v === null || v === undefined ? null : Number(v);
}

/** As contas que PODEM entrar num lote: abertas e com valor definido. O
 *  salário (094) fica de fora: ele se paga pela folha, linha a linha, e o
 *  lote de contas é uma transação de pagar_contas que não o conhece. */
export function pagaveisNoLote<T extends Pick<ContaPagar, 'pago_em' | 'valor_real' | 'valor_previsto'>>(
  lista: T[],
): T[] {
  return lista.filter((c) => !ehSalario(c) && !c.pago_em && valorDaConta(c) !== null);
}

/** Soma das selecionadas — só as que continuam na lista visível: id que o
 *  filtro escondeu não conta (e a tela zera a seleção ao filtrar). Salário
 *  não entra no lote, e portanto não entra nesta soma. */
export function totalSelecionado(
  lista: Array<Pick<ContaPagar, 'id' | 'valor_real' | 'valor_previsto'>>,
  selecao: ReadonlySet<string>,
): number {
  return lista
    .filter((c) => !ehSalario(c) && selecao.has(c.id))
    .reduce((soma, c) => soma + (valorDaConta(c) ?? 0), 0);
}

// --- O salário dentro de Contas a pagar (094, 08/10/2026) ----------------------
//
// A FOLHA É A ÚNICA FONTE. O salário não vira linha de contas_pagar: a tela
// lê payroll_entries e mostra cada linha AO LADO das contas, com a mesma forma
// que elas têm para listar, agrupar e somar. Pagar aqui é a mesma pagar_folha
// do Painel — por isso "marcar paga nos dois lugares" sai de graça: é a mesma
// linha.

/** As colunas que a tela de contas lê da folha — e nenhuma outra: comissão,
 *  bônus e descontos são do Painel. Array tipado pelo mesmo motivo do
 *  COLUNAS_DA_LISTA: o teste de embeds não confere select de colunas simples,
 *  e coluna com nome errado derruba a consulta só em produção. */
export const COLUNAS_DA_FOLHA = [
  'id', 'profile_id', 'competencia', 'total', 'vencimento', 'pago_em', 'forma_pagamento', 'prevista',
] as const satisfies ReadonlyArray<keyof PayrollEntry>;

export type LinhaDaFolhaNasContas = Pick<PayrollEntry, (typeof COLUNAS_DA_FOLHA)[number]> & {
  profiles: { nome: string } | null;
};

/** Uma linha da folha com a cara de uma conta: os campos que a tela usa para
 *  filtrar, agrupar e somar têm o mesmo nome e o mesmo sentido. */
export interface SalarioNaLista {
  origem: 'folha';
  id: string;
  profile_id: string;
  nome: string;
  competencia: string;
  descricao: string;
  categoria: 'salarios';
  /** Salário não é estimativa: previsto e real são o total, sem "~". */
  valor_previsto: number;
  valor_real: number;
  vencimento: string;
  /** O DIA DA LOJA do pagamento ('AAAA-MM-DD'), não o timestamptz cru. */
  pago_em: string | null;
  forma_pagamento: PaymentMethod | null;
  /** Nasceu do combinado e ninguém mexeu (094). */
  prevista: boolean;
  /** Prevista, em aberto, de quem tem comissão no combinado: o valor é só o
   *  salário-base, e ela não se paga daqui — a comissão se confere no Painel
   *  (revisão da 094; pagar_folha recusa o mesmo). Ver faltaAComissao. */
  falta_comissao: boolean;
  /** As contas canceladas saem de toda soma (086); salário não se cancela. */
  cancelada_em: null;
}

/** A linha é salário (094)? Funciona sobre qualquer item da lista mista. */
export function ehSalario(c: object): c is SalarioNaLista {
  return (c as { origem?: unknown }).origem === 'folha';
}

/**
 * A linha da folha vista como conta. Nulo = fica fora da lista: total zero
 * não é conta a pagar (é a prevista de quem teve o salário zerado, ou o
 * lançamento que o dono zerou para não pagar o mês).
 *
 * `pago_em` vira o dia da LOJA: na folha ele é timestamptz, e o pagamento
 * feito às 23h30 de Rio Grande já é o dia seguinte em UTC — fatiar a string
 * jogaria o salário no mês errado da aba "Pagas".
 */
export function salarioNaLista(
  l: LinhaDaFolhaNasContas,
  /** Quem tem comissão no combinado (employment_terms.comissao_percent > 0). */
  comComissao: ReadonlySet<string> = new Set(),
): SalarioNaLista | null {
  const total = Number(l.total);
  if (!Number.isFinite(total) || total <= 0) return null;
  const nome = l.profiles?.nome ?? 'sem nome';
  return {
    origem: 'folha',
    id: l.id,
    profile_id: l.profile_id,
    nome,
    competencia: l.competencia,
    descricao: `Salário — ${nome}`,
    categoria: 'salarios',
    valor_previsto: total,
    valor_real: total,
    // O mesmo recuo do fluxo de caixa: sem dia, a competência.
    vencimento: l.vencimento ?? l.competencia,
    pago_em: l.pago_em ? hojeISO(new Date(l.pago_em)) : null,
    forma_pagamento: l.forma_pagamento,
    prevista: l.prevista,
    falta_comissao: faltaAComissao(l, comComissao.has(l.profile_id) ? 1 : 0),
    cancelada_em: null,
  };
}

const DIAS_CURTOS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export interface GrupoDeVencimento<T> {
  /** 'YYYY-MM-DD' do vencimento. */
  dia: string;
  /** "Seg, 25/08" — o dia da semana é o que orienta quem paga ("segunda
   *  tem boleto"). */
  rotulo: string;
  atrasado: boolean;
  contas: T[];
  /** Soma só das contas COM valor. */
  subtotal: number;
  /** Quantas estão "a definir" — contadas, nunca somadas (046). */
  semValor: number;
}

/**
 * Agrupa contas ABERTAS por dia de vencimento, vencidas primeiro.
 *
 * É a resposta ao pedido "visualizar o vencimento de uma data específica":
 * em vez de uma lista plana, a tela vira um extrato do futuro — cada dia com
 * seu subtotal, legível durante a rolagem, sem precisar de calendário.
 *
 * `T12:00:00` na conversão: vencimento é data pura, e meia-noite UTC cairia
 * no dia anterior em Rio Grande (a armadilha de fuso de sempre).
 */
export function agruparPorVencimento<T extends Pick<ContaPagar, 'vencimento' | 'valor_real' | 'valor_previsto'>>(
  lista: T[],
  hoje: string,
): Array<GrupoDeVencimento<T>> {
  const mapa = new Map<string, GrupoDeVencimento<T>>();
  for (const c of lista) {
    const dia = c.vencimento;
    let g = mapa.get(dia);
    if (!g) {
      const d = new Date(`${dia}T12:00:00`);
      const rotulo = Number.isNaN(d.getTime())
        ? dia
        : `${DIAS_CURTOS[d.getDay()]}, ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
      g = { dia, rotulo, atrasado: dia < hoje, contas: [], subtotal: 0, semValor: 0 };
      mapa.set(dia, g);
    }
    g.contas.push(c);
    const v = valorDaConta(c);
    if (v === null) g.semValor += 1;
    else g.subtotal += v;
  }
  // Vencidas primeiro (mais antiga no topo — é a mais urgente), depois o
  // futuro em ordem de chegada.
  return [...mapa.values()].sort((a, b) => a.dia.localeCompare(b.dia));
}

export type AtalhoVencimento = 'hoje' | 'semana' | 'quinze' | 'tudo';

/** 'YYYY-MM-DD' + n dias corridos, sem escorregar de fuso: o meio-dia local
 *  não vira outro dia em nenhum fuso do Brasil. */
export function somarDias(iso: string, dias: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + dias);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Os chips de atalho só preenchem o filtro De/Até que a tela já tem —
 *  nenhum estado paralelo para divergir. */
export function atalhoDeVencimento(
  atalho: AtalhoVencimento,
  hoje: string,
): { de: string; ate: string } {
  if (atalho === 'tudo') return { de: '', ate: '' };
  if (atalho === 'hoje') return { de: hoje, ate: hoje };
  if (atalho === 'semana') return { de: hoje, ate: somarDias(hoje, 6) };
  return { de: hoje, ate: somarDias(hoje, 14) };   // 'quinze': hoje + 14 = 15 dias corridos
}

// --- A lista que vai para o pai do dono (22/09/2026) ---------------------------

/** As colunas que a lista enviada lê — e NENHUMA outra: a observação (texto
 *  livre) e os anexos nem chegam à memória do aparelho. É um array, e não uma
 *  string solta no select, para o TypeScript conferir cada nome contra a
 *  tabela: o teste de embeds não confere select de colunas simples, e coluna
 *  com nome errado derruba a consulta inteira só em produção. */
// `cancelada_em` entrou em 28/09/2026 (086): a tela precisa DISTINGUIR a conta
// cancelada, não só deixar de recebê-la. Sem ela na lista, "mostrar o que
// cancelei" seria impossível — e sem isso o cancelar vira um apagar mais
// educado, porque não dá para voltar atrás do que não se vê.
export const COLUNAS_DA_LISTA = [
  'id', 'descricao', 'categoria', 'valor_previsto', 'valor_real', 'vencimento', 'codigo_barras', 'pago_em',
  'cancelada_em', 'cancelada_por_nome', 'motivo_cancelamento', 'lote_id',
] as const satisfies ReadonlyArray<keyof ContaPagar>;

export type ContaParaLista = Pick<ContaPagar, (typeof COLUNAS_DA_LISTA)[number]>;

// --- Conta repetida e quem cobra (18/09/2026) ----------------------------------
//
// Pedido do dono: "cadastrei a mesma conta sem querer e não alertou nem nada".
// Dois avisos, de pesos diferentes:
//   - MESMO CÓDIGO DE BARRAS: é o mesmo boleto, sem discussão. A tela não
//     deixa salvar (e o banco também não — índice único da 078).
//   - MESMO VALOR com vencimento a até 3 dias: pode ser a mesma conta lançada
//     de novo (com o dia digitado errado) — ou duas contas iguais de verdade.
//     Avisa e pede confirmação; não impede.
// Parcela do mesmo fornecedor com o mesmo valor vence de 7 em 7 dias ou mais
// (é o que a loja tem), e por isso o corte é 3: pega o dia errado de digitação
// sem apitar a cada parcela.

type ContaComparavel = Pick<ContaPagar, 'id' | 'descricao' | 'vencimento' | 'valor_real' | 'valor_previsto'
  | 'pago_em' | 'codigo_barras' | 'categoria' | 'recorrencia_id'>;

const diasEntre = (a: string, b: string) =>
  Math.round((new Date(`${a}T12:00:00`).getTime() - new Date(`${b}T12:00:00`).getTime()) / 86_400_000);

export const DIAS_DE_FOLGA = 3;

/** O valor bate com o PREVISTO ou com o PAGO: conta paga com juros guarda o
 *  pago em valor_real, e o boleto lido de novo traz o nominal. */
const valorBate = (c: Pick<ContaPagar, 'valor_real' | 'valor_previsto'>, v: number) =>
  [c.valor_previsto, c.valor_real].some((x) => x !== null && x !== undefined && Math.abs(Number(x) - v) < 0.005);

/**
 * As contas que parecem ser a mesma que se está lançando:
 *   - mesmoCodigo: o mesmo boleto (a tela não deixa salvar; o banco também não);
 *   - parecidas: mesmo valor (previsto ou pago) com vencimento a até 3 dias;
 *     e, quando a categoria vem junto, a conta FIXA ainda sem valor definido
 *     ou só estimado (a luz "a definir" que a regra gerou), mesma categoria,
 *     a até 3 dias — o boleto dela É a conta do mês, não outra;
 *   - mesmoTitulo: a 2ª via ou prorrogação do mesmo boleto (mesmoTitulo),
 *     com qualquer data e valor.
 * Dois códigos DIFERENTES, fora o caso do mesmo título, são dois boletos.
 */
/**
 * A conta aberta que ainda espera a fatura: "a definir" (sem valor), ou com
 * a estimativa da regra perto do valor lido (±30% — a luz de 280 que veio 323;
 * o aluguel de 3.000 NÃO é a conta de um boleto de 150).
 */
const esperaFatura = (c: Pick<ContaPagar, 'valor_previsto' | 'valor_real' | 'recorrencia_id'>, valor: number | null) =>
  c.valor_real === null && (c.valor_previsto === null
    || (!!c.recorrencia_id && valor !== null && Math.abs(Number(c.valor_previsto) - valor) <= valor * 0.3));

export function contasParecidas<T extends ContaComparavel>(
  nova: {
    id?: string | null; valor: number | null; vencimento: string; codigo?: string | null;
    /** As categorias em que esta conta pode estar lançada (a do formulário,
     *  ou as do segmento do convênio). Vazio = não procura conta fixa. */
    categorias?: string[];
  },
  contas: T[],
  mesmoTituloDe: (a: string, b: string) => boolean = () => false,
): { mesmoCodigo: T | null; mesmoTitulo: T[]; parecidas: T[] } {
  const outras = contas.filter((c) => c.id !== nova.id);
  const mesmoCodigo = nova.codigo ? outras.find((c) => c.codigo_barras === nova.codigo) ?? null : null;
  const mesmoTitulo = nova.codigo
    ? outras.filter((c) => c.codigo_barras && mesmoTituloDe(nova.codigo!, c.codigo_barras))
    : [];
  const perto = (c: T) => !!nova.vencimento && Math.abs(diasEntre(c.vencimento, nova.vencimento)) <= DIAS_DE_FOLGA;
  const categorias = nova.categorias ?? [];
  const peloValor = (c: T) => nova.valor !== null && valorBate(c, nova.valor);
  const parecidas = outras
    .filter((c) => c !== mesmoCodigo && !mesmoTitulo.includes(c))
    .filter((c) => !(nova.codigo && c.codigo_barras && c.codigo_barras !== nova.codigo))
    .filter((c) => {
      if (!perto(c)) return false;
      if (peloValor(c)) return true;
      // A conta fixa do mês que ainda espera a fatura: o boleto que chega É ela.
      return !c.pago_em && !c.codigo_barras && categorias.includes(c.categoria) && esperaFatura(c, nova.valor);
    })
    // Quem bate pelo valor vem antes; depois, a mais perto no dia.
    .sort((a, b) => Number(peloValor(b)) - Number(peloValor(a))
      || Math.abs(diasEntre(a.vencimento, nova.vencimento)) - Math.abs(diasEntre(b.vencimento, nova.vencimento)));
  return { mesmoCodigo, mesmoTitulo, parecidas };
}

/**
 * Sem data no código (convênio: luz, água, telefone), as candidatas a "é esta"
 * são as contas em aberto e sem código da mesma categoria que vencem perto de
 * hoje — a conta do mês que a regra gerou, com o valor que for.
 */
export function contasDoMes<T extends ContaComparavel>(
  categorias: string[],
  valor: number | null,
  hoje: string,
  contas: T[],
): T[] {
  if (!categorias.length) return [];
  return contas
    .filter((c) => !c.pago_em && !c.codigo_barras && categorias.includes(c.categoria))
    .filter((c) => {
      const d = diasEntre(c.vencimento, hoje);
      return d >= -20 && d <= 40;
    })
    .filter((c) => (valor !== null && valorBate(c, valor)) || esperaFatura(c, valor))
    .sort((a, b) => Math.abs(diasEntre(a.vencimento, hoje)) - Math.abs(diasEntre(b.vencimento, hoje)));
}

/**
 * Convênio sem data no código: a conta JÁ PAGA com o mesmo valor, da mesma
 * categoria, nos últimos dois meses. Se houver, o boleto lido provavelmente é
 * o que já foi pago — e não a conta do mês que vem (revisão de 18/09/2026).
 */
export function pagasParecidas<T extends ContaComparavel>(
  categorias: string[],
  valor: number | null,
  hoje: string,
  contas: T[],
): T[] {
  if (!categorias.length || valor === null) return [];
  return contas
    .filter((c) => !!c.pago_em && !c.codigo_barras && categorias.includes(c.categoria) && valorBate(c, valor))
    .filter((c) => {
      const d = diasEntre(c.vencimento, hoje);
      return d >= -60 && d <= 10;
    })
    .sort((a, b) => b.vencimento.localeCompare(a.vencimento));
}

/**
 * Quem cobra este boleto, pelas contas que já têm código: a que tiver a
 * mesma conta de cobrança (semelhancaDeCobranca ≥ MESMA_COBRANCA) e o nome
 * que mais se repete entre elas. Devolve a mais recente desse nome — é dela
 * que o formulário copia descrição, categoria e fornecedor.
 */
export function reconhecerCobranca<T extends ContaComparavel & Pick<ContaPagar, 'categoria' | 'fornecedor_id'>>(
  linha: string,
  contas: T[],
  semelhanca: (a: string, b: string) => number,
  corte: number,
): { conta: T; vezes: number } | null {
  const grupos = new Map<string, T[]>();
  for (const c of contas) {
    if (!c.codigo_barras || c.codigo_barras === linha) continue;
    if (semelhanca(linha, c.codigo_barras) < corte) continue;
    const chave = c.descricao.normalize('NFKD').replace(/[^\p{L}\p{N}]/gu, '').toUpperCase();
    grupos.set(chave, [...(grupos.get(chave) ?? []), c]);
  }
  // Empate no tamanho: vence o grupo com o boleto mais recente — é o nome
  // que o dono usa hoje.
  const recente = (g: T[]) => g.reduce((m, c) => (c.vencimento > m ? c.vencimento : m), '');
  let melhor: T[] | null = null;
  for (const g of grupos.values()) {
    if (!melhor || g.length > melhor.length
        || (g.length === melhor.length && recente(g) > recente(melhor))) melhor = g;
  }
  if (!melhor) return null;
  const conta = [...melhor].sort((a, b) => b.vencimento.localeCompare(a.vencimento))[0]!;
  return { conta, vezes: melhor.length };
}
